<script>
  import Alert from '@leaflink/stash/Alert.vue';
  import Button from '@leaflink/stash/Button.vue';
  import tooltip from '@leaflink/stash/directives/tooltip';
  import Dropdown from '@leaflink/stash/Dropdown.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Menu from '@leaflink/stash/Menu.vue';
  import MenuItem from '@leaflink/stash/MenuItem.vue';
  import camelCase from 'lodash-es/camelCase';
  import isEmpty from 'lodash-es/isEmpty';
  import { storeToRefs } from 'pinia';

  import LicenseTypeChip from '@/components/LicenseTypeChip/LicenseTypeChip.vue';
  import LLPill from '@/components/LLPill';
  import SellerStatusBadge from '@/components/TrustedSeller/SellerStatusBadge';
  import { BADGE_KEYS, ROUTE_NAMES, SPLIT_NAMES } from '@/constants';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import api from '@/services/api';
  import { track } from '@/track';
  import formatPhoneNumber from '@/utils/formatPhoneNumber';

  import BrandBadge from './BrandBadge';
  import VendorMenuBrandSelect from './VendorMenuBrandSelect';

  export default {
    name: 'VendorMenuHeader',

    components: {
      Alert,
      BrandBadge,
      Button,
      Dropdown,
      Icon,
      LicenseTypeChip,
      LLPill,
      SellerStatusBadge,
      VendorMenuBrandSelect,
      Menu,
      MenuItem,
    },

    directives: {
      tooltip,
    },

    setup() {
      const { getIsSplitEnabled } = storeToRefs(useUserStore());

      return {
        isSplitEnabled: getIsSplitEnabled,
      };
    },

    props: {
      brand: {
        type: Object,
        default: () => ({}),
      },

      seller: {
        type: Object,
        default: () => ({}),
      },

      isPreview: Boolean,

      isPublicMenu: Boolean,

      isVendorMenuSplitEnabled: Boolean,

      facebookHandle: {
        type: String,
        default: '',
      },

      instagramHandle: {
        type: String,
        default: '',
      },

      twitterHandle: {
        type: String,
        default: '',
      },

      youtubeHandle: {
        type: String,
        default: '',
      },
    },

    emits: ['export', 'message'],

    data() {
      return {
        isClamped: true,
        isDescriptionTruncated: false,
        hideLearnMore: false,
        ROUTE_NAMES,
      };
    },

    computed: {
      isBrand() {
        return !isEmpty(this.brand);
      },

      isSeller() {
        return !isEmpty(this.seller);
      },

      isBrandSelectEnabled() {
        return this.vendor.brands.length && !this.isVendorMenuSplitEnabled;
      },

      /**
       * Populate the vendor object for brand or vendor menu
       */
      vendor() {
        // Object for vendor menu
        if (this.isSeller) {
          return {
            ...this.seller,
            address: this.seller.address || this.seller.city,
            placeholder: this.$t('shop.allBrands'),
          };
        }

        const brands = [...this.brand.company.additionalBrands];

        // All Brands option if the feature flag is enabled
        if (this.brand.company.enableVendorMenu && !this.isPreview && !this.isPublicMenu) {
          brands.push({ id: 0, name: this.$t('shop.allBrands') });
        }

        // Object for brand menu
        return {
          ...this.brand,
          address: this.brand.address || this.brand.city,
          brands,
          licenses: this.brand.company.licenses,
          sellerBadge: this.brand.badges.find((badge) => badge.key.includes('seller')) || {},
          socialEquityBadge: this.brand.badges.find((badge) => badge.key === BADGE_KEYS.SOCIAL_EQUITY_VERIFIED),
          paymentsBadge: this.brand.badges.find((badge) => badge.key === BADGE_KEYS.PAYMENTS),
        };
      },

      /**
       * Calculation of the categories in the proper order of display
       */
      displayCategories() {
        return [...this.vendor.productCategories]
          .sort((a, b) => a.id - b.id)
          .reduce((acc, curr, i) => (i === 0 ? curr.name : `${acc}, ${curr.name}`), '');
      },

      /**
       * Manualy hide the learn more message
       */
      showLearnMore() {
        const { company } = this.vendor;

        return (
          !this.isPreview && !company.previouslyOrdered && !company.hasCompanyRequestForBuyer && !this.hideLearnMore
        );
      },

      /**
       * Split to display social media links
       */
      canViewSocialLinks() {
        return this.isSplitEnabled(SPLIT_NAMES.CAN_VIEW_BRAND_MENU_SOCIAL_LINKS);
      },
    },

    mounted() {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          this.isDescriptionTruncated = entry.target.scrollHeight > entry.target.clientHeight;
        }
      });

      observer.observe(this.$refs.description);
    },

    methods: {
      camelCase,

      formatPhoneNumber,

      /**
       * This methods allow to change the brand menu page using another brand
       * @param {number} id The id from the brand to change to
       */
      changeBrand({ id, name, friendlySlug }) {
        if (this.isPublicMenu) {
          this.$router.push({ name: ROUTE_NAMES.PUBLIC_MENU, params: { brandSlug: friendlySlug } });

          return;
        }

        if (this.isPreview) {
          this.$router.push({ name: ROUTE_NAMES.SHOP_BRAND_PREVIEW, params: { brandId: id.toString() } });
        } else {
          track('Brand Menu Switched Brand', {
            From: this.vendor.name,
            To: name,
          });

          /**
           * If the option to go to vendor menu is pressed the id passed to this event is 0
           * otherwise the id from the brand would be a positive number
           */
          if (id) {
            this.$router.push({ name: ROUTE_NAMES.BRAND_MENU, params: { brandId: id.toString() } });
          } else {
            this.$router.push({
              name: ROUTE_NAMES.VENDOR_MENU,
              params: { sellerId: this.brand.company.id.toString() },
            });
          }
        }
      },

      goToVendorProfile() {
        const sellerId = this.isSeller ? this.vendor.id : this.brand.company.id.toString();

        this.$router.push({ name: ROUTE_NAMES.SELLER_DETAILS, params: { sellerId } });
      },

      /**
       * Send a learn more request
       */
      async sendLearnMoreRequest() {
        this.hideLearnMore = true;

        await api.shop.createLearnMoreRequest({
          brandId: this.vendor.id,
          sellerId: this.vendor.company.id,
        });

        this.$notify.success(this.$t('shop.learnMoreRequestSuccess'));
      },
    },
  };
</script>

<template>
  <div class="ll-mt-3">
    <div class="container">
      <section class="box ll-pa-0">
        <!-- Banner -->
        <img
          v-if="vendor.banner"
          class="rounded w-full"
          :alt="$t('brand_menu.brandBanner')"
          :class="$style.banner"
          :src="vendor.banner"
        />
        <div v-else-if="isPreview" class="w-full flex align-center ll-px-3" :class="$style.banner">
          <div :class="$style['preview-title']">
            <h3 class="color-red font-weight-medium text-center ll-mb-1">
              {{ $t('brands.previewMenu.missing.headerImage.header') }}
            </h3>
            <p class="color-ice-900">{{ $t('brands.previewMenu.missing.headerImage.paragraph') }}</p>
          </div>
        </div>

        <!-- Brand Information -->
        <div>
          <header class="rounded ll-mb-3" :class="$style.wrapper" data-test="brand|information-header">
            <div class="ll-grid ll-grid-cols-2 md:ll-grid-cols-8 relative lg:ll-grid-cols-12">
              <div class="col-span-1 md:col-span-3">
                <picture>
                  <!-- logo in square container, vertically centered/padded -->
                  <source
                    media="(min-width: 961px)"
                    :srcset="`https://res.cloudinary.com/leaflink/image/fetch/c_pad,w_670,q_auto:best/${vendor.image}`"
                  />
                  <!-- default logo, as-is (but scaled down for optimization) -->
                  <img
                    class="rounded w-full"
                    :alt="$t('brand_menu.brandLogo')"
                    :src="`https://res.cloudinary.com/leaflink/image/fetch/c_fit,w_670,q_auto:best/${vendor.image}`"
                  />
                </picture>

                <!-- More from this seller, desktop -->
                <VendorMenuBrandSelect
                  v-if="isBrandSelectEnabled"
                  class="col-span-2 md:col-span-4 lg:col-span-3 ll-mt-3"
                  :class="$style['brand-select']"
                  :show-seller-link="isBrand && !isPublicMenu"
                  :vendor="vendor"
                  @update:model-value="changeBrand"
                />
              </div>

              <!-- Brand socials  -->
              <div v-if="canViewSocialLinks" :class="['flex flex-row absolute mr-2', $style['social-icons']]">
                <a
                  v-if="facebookHandle"
                  :href="`https://www.facebook.com/${facebookHandle}`"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img alt="facebook-icon" :src="'/static/img/socials/facebook.svg'" />
                </a>
                <a
                  v-if="instagramHandle"
                  :href="`https://www.instagram.com/${instagramHandle}`"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img alt="instagram-icon" :src="'/static/img/socials/instagram.svg'" />
                </a>
                <a
                  v-if="twitterHandle"
                  :href="`https://www.twitter.com/${twitterHandle}`"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img alt="twitter-icon" :src="'/static/img/socials/twitter.svg'" />
                </a>
                <a
                  v-if="youtubeHandle"
                  :href="`https://www.youtube.com/${youtubeHandle}`"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img alt="youtube-icon" :src="'/static/img/socials/youtube.svg'" />
                </a>
              </div>

              <!-- Brand Profile and export action  -->
              <Dropdown v-if="!isPublicMenu && brand" :offset="{ x: 7 }" :class="$style.action">
                <Menu>
                  <MenuItem v-if="!isPreview">
                    <button @click="$emit('export')">
                      {{ $t('brand_menu.exportMenu') }}
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button @click="goToVendorProfile()">
                      {{ $t('shop.viewCompanyProfile') }}
                    </button>
                  </MenuItem>
                </Menu>
              </Dropdown>

              <!-- Main Information -->
              <div class="col-span-2 md:col-span-8 lg:col-span-9">
                <!-- Name and badges  -->
                <h1 class="ll-mt-0 ll-mr-4 ll-mb-1 flex" :class="$style.name">
                  {{ vendor.name }}
                  <span
                    v-if="isBrand"
                    v-tooltip.top="
                      $t(`shop.brands.badges.${camelCase(vendor.sellerBadge.group)}`, vendor.sellerBadge.params)
                    "
                  >
                    <SellerStatusBadge
                      v-if="vendor.sellerBadge.key"
                      class="ll-ml-1"
                      :class="$style.badge"
                      :status="vendor.sellerBadge.key"
                    />
                  </span>
                  <BrandBadge
                    v-if="vendor.paymentsBadge"
                    class="ll-ml-1"
                    :badge="vendor.paymentsBadge"
                    :seller="vendor"
                  />
                  <BrandBadge
                    v-if="vendor.socialEquityBadge"
                    class="ll-ml-1"
                    :badge="vendor.socialEquityBadge"
                    :seller="vendor"
                  />
                </h1>

                <!-- Social equity badges if they exist  -->
                <div v-if="isBrand && brand.selfIdentifiedBadges.length" class="col-span-6" :class="$style.pills">
                  <LLPill
                    v-for="badge in brand.selfIdentifiedBadges"
                    class="ll-mr-2 ll-mb-2"
                    :key="badge"
                    :label="badge"
                  />
                </div>

                <div class="flex flex-wrap">
                  <div v-for="license in vendor.licenses" :key="license.id" class="ll-mr-2 ll-mb-2">
                    <LicenseTypeChip class="ll-mr-1" :license="license.license" :title="license.license.number" />
                    <a
                      v-if="!isPublicMenu"
                      :for="license.license.id"
                      :class="['text-small', { [$style['private-license']]: !license.public || !license.url }]"
                      :href="license.url"
                      rel="noopener"
                      target="_blank"
                      >{{ license.license.number }}</a
                    >
                  </div>
                </div>
                <!-- Vendor Product Categories -->
                <p v-if="isBrand" class="text-small ll-mb-3">{{ displayCategories }}</p>
                <!-- Vendor Tagline -->
                <p v-if="vendor.tagline" class="heading-4 ll-mb-1">{{ vendor.tagline }}</p>
                <div ref="description" :class="[$style.description, { [$style['clamp-description']]: isClamped }]">
                  <p v-html="vendor.description"></p>
                </div>
                <Button
                  v-if="isDescriptionTruncated || !isClamped"
                  inline
                  class="font-weight-medium block"
                  @click="isClamped = !isClamped"
                >
                  {{ isClamped ? $t('common.readMore') : $t('common.readLess') }}
                </Button>
                <div class="flex flex-wrap align-top ll-mt-3" :class="$style.links">
                  <a
                    v-if="vendor.website"
                    class="heading-4 color-blue ll-mr-2"
                    rel="noopener"
                    target="_blank"
                    :class="$style.link"
                    :href="vendor.website"
                  >
                    <Icon name="link" size="dense" />
                    {{ vendor.website.replace(/^(?:http(s?)?:\/\/)?(?:www\.)?/i, '') }}
                  </a>
                  <a
                    v-if="vendor.phone"
                    class="heading-4 color-blue ll-mr-2"
                    :href="`tel:${vendor.phone}`"
                    :class="$style.link"
                  >
                    <Icon name="phone" size="dense" /> {{ formatPhoneNumber(vendor.phone) }}
                  </a>
                  <a
                    v-if="vendor.address"
                    class="heading-4 color-blue ll-mr-2"
                    :href="`https://www.google.com/maps/place/${vendor.address}`"
                    :class="$style.link"
                    rel="noopener"
                    target="_blank"
                  >
                    <Icon name="location" size="dense" /> {{ vendor.address }}
                  </a>
                  <Button v-if="!isPublicMenu" inline class="heading-4 color-blue" @click="$emit('message')">
                    <Icon name="message" size="dense" /> Message
                  </Button>
                </div>
              </div>

              <!-- More from this seller, mobile -->
              <VendorMenuBrandSelect
                v-if="isBrandSelectEnabled"
                class="col-span-2 md:col-span-4 hidden-lg"
                :show-seller-link="isBrand && !isPublicMenu"
                :vendor="vendor"
                @update:model-value="changeBrand"
              />
            </div>
          </header>
        </div>
      </section>
    </div>

    <!-- Brand Announcement -->
    <aside v-if="!isPublicMenu && vendor.announcement" class="container">
      <Alert severity="info">
        {{ vendor.announcement }}
      </Alert>
    </aside>

    <!-- Learn More -->
    <aside v-if="isBrand && !isPublicMenu && showLearnMore" class="container">
      <Alert severity="info">
        {{ $t('brand_menu.learnMore', { company: vendor.company.name }) }}
        <Button inline @click="sendLearnMoreRequest">
          {{ $t('brand_menu.letThemKnow') }}
        </Button>
      </Alert>
    </aside>
  </div>
</template>

<style module lang="scss">
  .banner {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    max-height: 420px;
    object-fit: cover;
  }

  .preview {
    background: var(--color-red-100);
    border: border('thin', var(--color-red));
    height: 300px;
  }

  .preview-title {
    margin-top: space(3);

    @include breakpoint('md') {
      margin-top: space(5);
    }
  }

  .wrapper {
    @include elevation('low'); // TODO: Remove this when is included in @leaflink/stash

    padding: var(--grid-gutter);
  }

  .name {
    align-self: center;
    font-size: typo('h2', 'size');

    @include breakpoint('lg') {
      align-self: normal;
      font-size: typo('h1', 'size');
    }
  }

  .pills {
    @include breakpoint('lg') {
      grid-column-start: 4;
    }
  }

  .badge {
    height: 18px;
    vertical-align: baseline;
    width: 18px;
  }

  div.action {
    position: absolute;
    right: 0;
    top: 0;
  }

  .description > p:last-child {
    margin-bottom: 0;
  }

  .clamp-description {
    @include clamp(5);
  }

  .links {
    flex-direction: column;
    row-gap: space(2);

    @include breakpoint('lg') {
      flex-direction: row;
    }
  }

  .private-license {
    color: var(--color-ice-700);
    cursor: default;

    &:hover {
      text-decoration: none;
    }
  }

  .brand-select {
    display: none;

    @include breakpoint('lg') {
      display: flex;
    }
  }

  .social-icons {
    right: 50px;
    top: 10px;
  }
</style>
